import { Box, Typography } from '@mui/material';
import FlexBetween from 'components/FlexBetween';
import { RULER_H, RULER_TOP } from './constants';

const PERIOD_CFG = [
  { h: 0,  label: 'Night',     shortLabel: '00', bg: 'rgba(10,20,80,0.70)',   color: '#eff1f5',  dimColor: 'rgba(240,242,247,0.90)' },
  { h: 6,  label: 'Morning',   shortLabel: '06', bg: 'rgba(160,110,0,0.45)',  color: '#ffe680',  dimColor: 'rgba(255,230,128,0.55)' },
  { h: 12, label: 'Afternoon', shortLabel: '12', bg: 'rgba(20,70,40,0.50)',   color: '#a8f0c0',  dimColor: 'rgba(168,240,192,0.55)' },
  { h: 18, label: 'Evening',   shortLabel: '18', bg: 'rgba(120,35,5,0.60)',   color: '#ffc88a',  dimColor: 'rgba(255,200,138,0.55)' },
];

const Ruler = ({ totalW }) => {
  const cellW    = totalW / 24;
  const periodW  = totalW / 4;          // exact integer-free quarter
  const hourStep = cellW < 35 ? 4 : cellW < 55 ? 2 : 1;
  const showLabel = periodW >= 52;
  const numCells  = 24 / hourStep;
  const cellWStep = totalW / numCells;  // exact cell width without float drift

  return (
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 3,
      height: RULER_H, width: totalW,
      bgcolor: '#1e242c',
      borderBottom: '2px solid #3d4f5c',
      flexShrink: 0,
      overflow: 'hidden',
    }}>
      {/* Top row: 4 × 6h period bands — explicit left+width, no right:0 */}
      {PERIOD_CFG.map(({ h, label, shortLabel, bg, color, dimColor }, idx) => (
        <FlexBetween
          key={h}
          sx={{
            position: 'absolute',
            top: 0,
            left: periodW * idx,
            width: periodW,
            height: RULER_TOP,
            bgcolor: bg, gap: 0.5, px: 0.75,
            borderRight: '1px solid rgba(255,255,255,0.07)',
            overflow: 'hidden',
          }}
        >
          {showLabel && (
            <Typography sx={{ fontSize: '0.65rem', color, fontFamily: 'monospace', fontWeight: 600, letterSpacing: 0.8, userSelect: 'none', lineHeight: 1 }}>
              {label}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.53rem', color: dimColor, fontFamily: 'monospace', userSelect: 'none', lineHeight: 1 }}>
            {showLabel ? `${shortLabel}-${String(h + 6).padStart(2, '0')}` : shortLabel}
          </Typography>
        </FlexBetween>
      ))}

      {/* Separator */}
      <Box sx={{ position: 'absolute', top: RULER_TOP, left: 0, width: totalW, height: 1, bgcolor: '#3d4f5c' }} />

      {/* Bottom row: hour cells — explicit left+width, no right:0 */}
      {Array.from({ length: numCells }, (_, i) => {
        const h       = i * hourStep;
        const isMajor = h % 6 === 0;
        const isNight = h < 6 || h >= 22;
        const cfg     = PERIOD_CFG[Math.floor(h / 6)] || PERIOD_CFG[3];
        return (
          <FlexBetween
            key={h}
            sx={{
              position: 'absolute',
              top: RULER_TOP + 1,
              left: cellWStep * i,
              width: cellWStep,
              bottom: 0,
              borderRight: `1px solid ${isMajor ? '#3d4f5c' : 'rgba(255,255,255,0.07)'}`,
              bgcolor: isMajor ? 'rgba(255,255,255,0.025)' : 'transparent',
            }}
          >
            <Typography sx={{
              fontSize: isMajor ? '0.64rem' : '0.65rem',
              color: isNight ? cfg.dimColor : cfg.color,
              opacity: isMajor ? 1 : 0.75,
              fontFamily: 'monospace',
              fontWeight: isMajor ? 700 : 400,
              userSelect: 'none',
              lineHeight: 1,
            }}>
              {String(h).padStart(2, '0')}
            </Typography>
          </FlexBetween>
        );
      })}

      {/* Night shading — explicit left+width, no right:0 */}
      <Box sx={{ position: 'absolute', left: 0, width: totalW / 4, top: RULER_TOP + 1, bottom: 0, bgcolor: 'rgba(0,0,0,0.18)', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', left: (22 / 24) * totalW, width: (2 / 24) * totalW, top: RULER_TOP + 1, bottom: 0, bgcolor: 'rgba(0,0,0,0.18)', pointerEvents: 'none' }} />
    </Box>
  );
};

export default Ruler;
